<template>
	<AppLayout :title="trans('events.requests.title')">
		<template v-if="canCreate" #actions>
			<Link :as="PrimaryButton" :color="Color.Green" :href="route('events.requests.create')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</Link>
		</template>
		<Empty v-if="requests.length === 0"/>
		<CardGrid v-else>
			<Card v-for="request in requests" :key="`period-request-${request.id}`" :title="`${trans('general.quotes.open')}${request.lemma}${trans('general.quotes.close') }`">
				<Modal :id="`edit-request-${request.id}-modal`">
					<template #title>{{ trans('requests.actions.details') }}</template>
					<template v-if="request">
						<Badge :color="Color.TrueAArEII" class="place-self-start" active>{{ trans(`events.${request.type}.title`) }}</Badge>
						<LabeledObject>
							<template #label>{{ trans('events.requests.location') }}</template>
							<p>{{ request.location.name }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.requests.colleges') }}</template>
							<p>{{ request.colleges.map((college:College) => college.name).join(', ') }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.requests.email') }}</template>
							<p>{{ request.email }}</p>
						</LabeledObject>
						<LabeledObject v-if="request.name">
							<template #label>{{ trans('events.requests.name') }}</template>
							<p>{{ request.name }}</p>
						</LabeledObject>
						<LabeledObject v-if="request.lemma">
							<template #label>{{ trans('events.requests.lemma') }}</template>
							<p>{{ request.lemma }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.requests.begins_on') }}</template>
							<p>{{ dayjs(request.begins_on).format(trans('general.dayjs.date')) }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.requests.ends_on') }}</template>
							<p>{{ dayjs(request.ends_on).format(trans('general.dayjs.date')) }}</p>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.requests.online') }}</template>
							<div v-if="request.online" class="flex items-center gap-2 text-green-500">
								<i class="fa-solid fa-check"/>
								<p>{{ trans('general.yes') }}</p>
							</div>
							<div v-else class="flex items-center gap-2 text-red-500">
								<i class="fa-solid fa-times"/>
								<p>{{ trans('general.no') }}</p>
							</div>
						</LabeledObject>
						<LabeledObject>
							<template #label>{{ trans('events.requests.estimated_participants') }}</template>
							<p>{{ request.estimated_participants }}</p>
						</LabeledObject>
						<LabeledObject v-if="request.observations">
							<template #label>{{ trans('events.requests.observations') }}</template>
							<p>{{ request.observations }}</p>
						</LabeledObject>
					</template>
				</Modal>
				<template #badges>
					<Badge :color="request.colors" active>{{ trans(`requests.status.${request.status}`) }}</Badge>
					<i v-if="request.online" class="fa-solid fa-video-camera text-gray-400 dark:text-gray-600"/>
					<Badge :color="Color.TrueAArEII" active>{{ trans(`events.${request.type}.title`) }}</Badge>
				</template>
				<LabeledObject>
					<template #label>{{ trans('events.requests.begins_on') }}</template>
					<p>{{ dayjs(request.begins_on).format(trans('general.dayjs.date')) }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.requests.ends_on') }}</template>
					<p>{{ dayjs(request.ends_on).format(trans('general.dayjs.date')) }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.requests.colleges') }}</template>
					<p>{{ request.colleges.map((college:College) => college.name).join(', ') }}</p>
				</LabeledObject>
				<template #footer>
					<SecondaryButton :color="Color.Blue" @click.stop="showModal(`edit-request-${request.id}-modal`)">
						<i class="fa-solid fa-file-text mr-2"/>
						{{ trans('requests.actions.details') }}
					</SecondaryButton>
				</template>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {ref, type Ref} from 'vue'
import {showModal} from '@/helpers'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/Empty.vue'
import Card from '@/Components/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/Modal/Modal.vue'
import Badge from '@/Components/Badges/Badge.vue'
import {type College} from '@/Types/Settings/College'
import CardGrid from '@/Components/Cards/CardGrid.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {type EventRequest} from '@/Types/Periods/Events/EventRequest'

const props = defineProps<{
	canCreate:boolean,
	canUpdate:boolean,
	requests:Array<EventRequest>,
}>()

const requests:Ref<Array<EventRequest>> = ref(props.requests)
</script>